/**
 * Markdown-leichte Darstellung der Coach-Antwort (1:1 zur Web-App):
 * fette Stellen, Inline-Code, Überschriften (#), Aufzählungen und Absätze.
 * Rendert sichere Preact-Knoten (kein dangerouslySetInnerHTML).
 */
import type { JSX } from "preact";
import { Fragment } from "preact";
import { parseJumpTarget } from "../state/course-nav";

let keySeq = 0;

function inline(text: string): (JSX.Element | string)[] {
  const nodes: (JSX.Element | string)[] = [];
  // Reihenfolge: Markdown-Link [Text](ziel) | **fett** | `code`. Das Ziel wird
  // erst grob abgegriffen und dann STRENG gegen das Kurs-Schema validiert
  // (parseJumpTarget) — passt es nicht exakt, bleibt der Link reiner Text. So
  // entstehen niemals beliebige/`javascript:`-URLs (kein dangerouslySetInnerHTML).
  const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      const jump = parseJumpTarget(m[2]);
      if (jump) {
        nodes.push(
          <a key={keySeq++} class="chat-jump" data-m={jump.m} data-block={jump.b} data-u={jump.u} href="#">
            {m[1]}
          </a>,
        );
      } else {
        // Kein gültiges Kurs-Ziel → reiner Text (inkl. der Markdown-Syntax).
        nodes.push(text.slice(m.index, regex.lastIndex));
      }
    } else if (m[3] !== undefined) nodes.push(<strong key={keySeq++}>{m[3]}</strong>);
    else if (m[4] !== undefined) nodes.push(<code key={keySeq++}>{m[4]}</code>);
    last = regex.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function formatAssistant(text: string): JSX.Element[] {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const out: JSX.Element[] = [];
  let para: string[] = [];
  let list: string[] = [];

  const flushPara = () => {
    if (!para.length) return;
    const children: (JSX.Element | string)[] = [];
    para.forEach((l, i) => {
      if (i > 0) children.push(<br key={keySeq++} />);
      inline(l).forEach((n) => children.push(n));
    });
    out.push(<p key={keySeq++}>{children}</p>);
    para = [];
  };
  const flushList = () => {
    if (!list.length) return;
    const items = list;
    out.push(
      <ul key={keySeq++}>
        {items.map((li) => (
          <li key={keySeq++}>{inline(li)}</li>
        ))}
      </ul>,
    );
    list = [];
  };

  for (const raw of lines) {
    const t = raw.trim();
    if (!t) {
      flushPara();
      flushList();
      continue;
    }
    const heading = t.match(/^#{1,3}\s+(.+)/);
    if (heading) {
      flushPara();
      flushList();
      out.push(<h4 key={keySeq++}>{inline(heading[1])}</h4>);
      continue;
    }
    const bullet = t.match(/^[-*•]\s+(.+)/) || t.match(/^\d+[.)]\s+(.+)/);
    if (bullet) {
      flushPara();
      list.push(bullet[1]);
      continue;
    }
    flushList();
    para.push(t);
  }
  flushPara();
  flushList();
  return out.length ? out : [<Fragment key={keySeq++} />];
}
